import { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Set worker using the bundled worker from pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface PDFViewerProps {
  url: string;
  className?: string;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;

export function PDFViewer({ url, className = '' }: PDFViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [scale, setScale] = useState(1);
  const [fitScale, setFitScale] = useState(1);

  // Drag state for pan functionality
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);
  const touchStartPos = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);

      try {
        // Don't encode URLs that are already encoded (e.g., signed URLs from storage)
        // Only encode if it's not a blob URL and doesn't contain encoded characters or query params
        let normalizedUrl = url;
        if (!url.startsWith('blob:') && !url.includes('%') && !url.includes('?')) {
          normalizedUrl = encodeURI(url);
        }

        const pdf = await pdfjsLib.getDocument(normalizedUrl).promise;
        if (cancelled) {
          pdf.destroy();
          return;
        }
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setCurrentPage(1);
        setScale(1);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF:', err);
          setError('Failed to load PDF document');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Cleanup PDF document on unmount
  useEffect(() => {
    return () => {
      pdfDoc?.destroy();
    };
  }, [pdfDoc]);

  // Calculate fit scale when container or PDF changes
  useEffect(() => {
    const calculateFitScale = async () => {
      if (!pdfDoc || !containerRef.current) return;
      
      try {
        const page = await pdfDoc.getPage(1);
        const containerWidth = containerRef.current.clientWidth - 32;
        const originalViewport = page.getViewport({ scale: 1 });
        const newFitScale = Math.min(containerWidth / originalViewport.width, 1.5);
        setFitScale(newFitScale);
      } catch (err) {
        console.error('Error calculating fit scale:', err);
      }
    };

    calculateFitScale();
  }, [pdfDoc]);

  // Render current page
  useEffect(() => {
    let cancelled = false;

    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

      // Cancel any in-progress render
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: fitScale * scale });

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context || cancelled) return;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport,
          canvas: canvas,
        });

        renderTaskRef.current = renderTask;

        await renderTask.promise;
        renderTaskRef.current = null;
      } catch (err: unknown) {
        // Ignore cancellation errors
        if (err instanceof Error && err.message?.includes('Rendering cancelled')) {
          return;
        }
        if (!cancelled) {
          console.error('Error rendering page:', err);
          setError('Failed to render page');
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale, fitScale]);

  // Clear drag state when page changes
  useEffect(() => {
    setIsDragging(false);
    dragStartPos.current = null;
    touchStartPos.current = null;
  }, [currentPage]);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE));
    setIsDragging(false);
    dragStartPos.current = null;
    touchStartPos.current = null;
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - SCALE_STEP, MIN_SCALE));
    setIsDragging(false);
    dragStartPos.current = null;
    touchStartPos.current = null;
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1);
    setIsDragging(false);
    dragStartPos.current = null;
    touchStartPos.current = null;
  }, []);

  // Mouse drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // Only left click

    const container = canvasContainerRef.current;
    if (!container) return;

    // Only enable drag if content is scrollable
    const isScrollable =
      container.scrollHeight > container.clientHeight ||
      container.scrollWidth > container.clientWidth;

    if (!isScrollable) return;

    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    e.preventDefault(); // Prevent text selection
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStartPos.current || !canvasContainerRef.current) return;

    e.preventDefault();
    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;

    canvasContainerRef.current.scrollLeft = dragStartPos.current.scrollLeft - deltaX;
    canvasContainerRef.current.scrollTop = dragStartPos.current.scrollTop - deltaY;
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartPos.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartPos.current = null;
    }
  }, [isDragging]);

  // Touch drag handlers
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const container = canvasContainerRef.current;
    if (!container || e.touches.length !== 1) return;

    const isScrollable =
      container.scrollHeight > container.clientHeight ||
      container.scrollWidth > container.clientWidth;

    if (!isScrollable) return;

    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartPos.current || !canvasContainerRef.current || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartPos.current.x;
    const deltaY = touch.clientY - touchStartPos.current.y;

    canvasContainerRef.current.scrollLeft = touchStartPos.current.scrollLeft - deltaX;
    canvasContainerRef.current.scrollTop = touchStartPos.current.scrollTop - deltaY;

    e.preventDefault(); // Prevent page scroll
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    touchStartPos.current = null;
  }, []);

  // Dynamic cursor class helper
  const getCursorClass = useCallback(() => {
    if (!canvasContainerRef.current) return 'cursor-default';

    const container = canvasContainerRef.current;
    const isScrollable =
      container.scrollHeight > container.clientHeight ||
      container.scrollWidth > container.clientWidth;

    if (!isScrollable) return 'cursor-default';
    if (isDragging) return 'cursor-grabbing';
    return 'cursor-grab';
  }, [isDragging]);

  const zoomPercentage = Math.round(scale * 100);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-muted-foreground ${className}`}>
        <AlertCircle className="w-12 h-12 mb-2" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`flex flex-col overflow-hidden ${className}`}>
      {/* Zoom Controls */}
      <div className="flex items-center justify-center gap-1 py-2 border-b border-border bg-muted/30">
        <Button
          variant="ghost"
          size="sm"
          onClick={zoomOut}
          disabled={scale <= MIN_SCALE}
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-xs text-muted-foreground tabular-nums w-12 text-center">
          {zoomPercentage}%
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={zoomIn}
          disabled={scale >= MAX_SCALE}
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetZoom}
          disabled={scale === 1}
          title="Fit to Width"
        >
          <Maximize className="w-4 h-4" />
        </Button>
      </div>

      {/* PDF Canvas */}
      <div className="flex-1 min-h-0 relative">
        <div
          ref={canvasContainerRef}
          className={cn(
            "absolute inset-0 overflow-auto p-4 bg-muted/20 select-none",
            getCursorClass()
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <canvas ref={canvasRef} className="shadow-md block mx-auto" />
        </div>
      </div>

      {/* Page Navigation */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2 border-t border-border bg-muted/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            {currentPage} / {numPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
            disabled={currentPage === numPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
